import styled from 'styled-components';
import { OrderAnalysisChart } from '../../../component/chart/OrderAnalysisChart';
import { OrderAmountRankChart } from '../../../component/chart/OrderAmountRankChart';
import { OrderCountRankChart } from '../../../component/chart/OrderCountRankChart';
import { ChartByCustomerGroup } from '../../../component/chart/ChartByCustomerGroup';
import { ChartByProdAmountPrice } from '../../../component/chart/ChartByProdAmountPrice';
import { YearlyAnalysisTable } from '../../../component/table/TableComponents/YearlyAnalysisTable';
import { useHeaderButtons } from '../../../component/header/HeaderButtonProvider';
import { getMonthStartEnd } from '../../../function/replaceString';
import { useEffect, useState } from 'react';
import SquareButton from '../../../component/button/SquareButton';
import {
  OrderAmountRankChartProvider,
  useOrderAmountRankChart,
} from '../../../component/chart/provider/OrderAmountRankChartProvider';
import { ChartController } from '../../../component/chart/controller/ChartController';
import {
  OrderCountRankChartProvider,
  useOrderCountRankChart,
} from '../../../component/chart/provider/OrderCountRankChartProvider';
import {
  OrderAnalysisChartProvider,
  useOrderAnalysisChart,
} from '../../../component/chart/provider/OrderAnalysisChartProvider';
import toast from 'react-hot-toast';
import { ChartControllerEss } from '../../../component/chart/controller/ChartControllerEss';
import {
  ProdAmountPriceChartProvider,
  useProdAmountPriceChart,
} from '../../../component/chart/provider/ProdAmountPriceChartProvider';
import { ProdAmountPriceBarChart } from '../../../component/chart/ProdAmountPriceBarChart';
import {
  MonthlyGroupedResultChartProvider,
  useMonthlyGroupedResultChart,
} from '../../../component/chart/provider/MonthlyGroupedResultChartProvider';
import {
  DeliveryTypePercentageProvider,
  useDeliveryTypePercentageData,
} from '../../../component/chart/provider/DeliveryTypePercentProvider';
import {
  OrderTypePercentageProvider,
  useOrderTypePercentageData,
} from '../../../component/chart/provider/OrderTypePercentageProvider';
import { OrderTypeChart } from '../../../component/chart/OrderTypeChart';
import { DeliveryTypeChart } from '../../../component/chart/DeliveryTypeChart';
import {
  adjustEndDate,
  calculateMonthsDifference,
  isValidRange,
} from '../../../function/dateVerify';

export const ChartAnalysisPage = () => {
  const { setButtons } = useHeaderButtons();
  const [periodStartDate, setPeriodStartDate] = useState<string>(
    getMonthStartEnd(new Date()).startDash
  );

  const [periodEndDate, setPeriodEndDate] = useState<string>(
    getMonthStartEnd(new Date()).endDash
  );

  const [fetchCount, setFetchCount] = useState<number>(0);

  useEffect(() => {
    //헤더에 버튼 추가
    setButtons(
      <>
        <SquareButton
          text='조회하기'
          colorType='ACTIVE'
          onClick={() => setFetchCount(fetchCount + 1)}
        />
      </>
    );
    // 페이지가 바뀌면 버튼 초기화
    return () => setButtons(null);
  }, [setButtons, periodStartDate, periodEndDate, fetchCount]);

  // 시작일 변경 처리
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;

    if (periodEndDate && !isValidRange(newStartDate, periodEndDate)) {
      return;
    }

    setPeriodStartDate(newStartDate);

    if (periodEndDate) {
      // 시작일 변경 시 종료일 조정
      const monthsDiff = calculateMonthsDifference(newStartDate, periodEndDate);
      if (monthsDiff >= 12) {
        // 종료일이 12개월을 초과하는 경우, 종료일을 12개월 내로 조정
        const adjustedEndDate = adjustEndDate(newStartDate);
        const newEndDateStr = adjustedEndDate.toISOString().split('T')[0];
        setPeriodEndDate(newEndDateStr);
        toast.error('기간이 12개월을 초과하여 자동으로 조정되었습니다');
      }
    }
  };

  // 종료일 변경 처리
  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value;

    if (periodStartDate && !isValidRange(periodStartDate, newEndDate)) {
      return;
    }

    setPeriodEndDate(newEndDate);

    // 종료일 변경 시 월 수가 12개월을 넘지 않도록 조정
    const monthsDiff = calculateMonthsDifference(periodStartDate, newEndDate);
    if (monthsDiff >= 12) {
      // 종료일이 12개월을 초과하는 경우, 종료일을 12개월 내로 조정
      const adjustedEndDate = adjustEndDate(periodStartDate);
      const newEndDateStr = adjustedEndDate.toISOString().split('T')[0];
      setPeriodEndDate(newEndDateStr);
      toast.error('기간이 12개월을 초과하여 자동으로 조정되었습니다');
    }
  };

  return (
    <Container>
      <PeriodContainer>
        <Row>
          <RowLabel>조회시작일</RowLabel>
          <RowContent>
            <DateInput
              type='date'
              value={periodStartDate}
              onChange={handleStartDateChange}
            />
          </RowContent>
          <RowLabel>조회종료일</RowLabel>
          <RowContent>
            <DateInput
              type='date'
              value={periodEndDate}
              onChange={handleEndDateChange}
            />
          </RowContent>
        </Row>
      </PeriodContainer>
      {fetchCount > 0 && (
        <>
          <ChartContainer>
            <OrderAnalysisChartProvider
              fetchCount={fetchCount}
              startDt={periodStartDate}
              endDt={periodEndDate}
            >
              <ChartControllerEss
                contextCall={useOrderAnalysisChart}
                title='월별 주문총액 및 주문횟수'
              />
              <OrderAnalysisChart />
            </OrderAnalysisChartProvider>
          </ChartContainer>
          <FlexRowContainer>
            {/* 거래처별 주문총액 데이터 */}
            <ChartContainer>
              <OrderAmountRankChartProvider
                fetchCount={fetchCount}
                startDt={periodStartDate}
                endDt={periodEndDate}
              >
                <ChartController
                  contextCall={useOrderAmountRankChart}
                  title='거래처별 주문총액 데이터'
                />
                <OrderAmountRankChart />
              </OrderAmountRankChartProvider>
            </ChartContainer>
            {/* 거래처별 주문건수 데이터 */}
            <ChartContainer>
              <OrderCountRankChartProvider
                fetchCount={fetchCount}
                startDt={periodStartDate}
                endDt={periodEndDate}
              >
                <ChartController
                  contextCall={useOrderCountRankChart}
                  title='거래처별 주문횟수 데이터'
                />
                <OrderCountRankChart />
              </OrderCountRankChartProvider>
            </ChartContainer>
          </FlexRowContainer>
          {/* 거래처 기관그룹별 매출 추이 */}
          <ChartContainer>
            <MonthlyGroupedResultChartProvider
              fetchCount={fetchCount}
              startDt={periodStartDate}
              endDt={periodEndDate}
            >
              <ChartControllerEss
                contextCall={useMonthlyGroupedResultChart}
                title='거래처 그룹별 매출 추이'
              />
              <ChartByCustomerGroup />
            </MonthlyGroupedResultChartProvider>
          </ChartContainer>
          <FlexRowContainer>
            {/* 품목 판매비율 데이터 파이형*/}
            <ChartContainer>
              <ProdAmountPriceChartProvider
                fetchCount={fetchCount}
                startDt={periodStartDate}
                endDt={periodEndDate}
              >
                <ChartControllerEss
                  contextCall={useProdAmountPriceChart}
                  title='품목 판매비율'
                />
                <ChartByProdAmountPrice />
              </ProdAmountPriceChartProvider>
            </ChartContainer>
            <ChartContainer>
              <ProdAmountPriceChartProvider
                fetchCount={fetchCount}
                startDt={periodStartDate}
                endDt={periodEndDate}
              >
                <ChartController
                  contextCall={useProdAmountPriceChart}
                  title='품목 판매량'
                />
                <ProdAmountPriceBarChart />
              </ProdAmountPriceChartProvider>
            </ChartContainer>
          </FlexRowContainer>

          <FlexRowContainer>
            {/* 배송방법 비율 차트 */}
            <ChartContainer>
              <DeliveryTypePercentageProvider
                fetchCount={fetchCount}
                startDt={periodStartDate}
                endDt={periodEndDate}
              >
                <ChartControllerEss
                  contextCall={useDeliveryTypePercentageData}
                  title='배송방법 비율'
                />
                <DeliveryTypeChart />
              </DeliveryTypePercentageProvider>
            </ChartContainer>
            {/* 주문방법 비율 차트 */}
            <ChartContainer>
              <OrderTypePercentageProvider
                fetchCount={fetchCount}
                startDt={periodStartDate}
                endDt={periodEndDate}
              >
                <ChartControllerEss
                  contextCall={useOrderTypePercentageData}
                  title='주문방법 비율'
                />
                <OrderTypeChart />
              </OrderTypePercentageProvider>
            </ChartContainer>
          </FlexRowContainer>
          {/* 품목 판매비율 데이터 막대형*/}
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  * {
    box-sizing: border-box;
  }
`;

const ChartTitle = styled.h3``;

const ChartContainer = styled.div<{ $width?: string }>`
  /* width: 80%; */
  /* flex: 1; */
  width: ${({ $width }) => ($width ? $width : '100%')};
  /* width: 800px; */
  height: fit-content;
  background-color: white;
  padding: 14px 18px;
`;

const FlexRowContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
`;

const PeriodContainer = styled.div`
  max-width: 800px;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-items: stretch;

  &:not(:last-child) {
    border-bottom: 1px solid #b8b8b8;
  }
`;
const RowLabel = styled.div`
  flex: 0 0 120px;
  background-color: #d9d9d9;
  font-size: 15px;
  align-content: center;
  text-align: center;
  padding: 4px 0px;
  font-weight: 700;
`;

const RowContent = styled.div`
  flex-grow: 1;
  padding: 4px 10px;
  background-color: white;
`;

const DateInput = styled.input`
  width: 100%;
  color: #595b60;
  font-weight: 700;
  font-size: 14px;
  text-align: center;
  box-sizing: border-box;
  border: none;
  cursor: pointer;

  /* Webkit 브라우저에서 달력 아이콘 숨기기 */
  /* &::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }

  /* Firefox 브라우저에서 달력 아이콘 숨기기 */
  /*-moz-appearance: textfield; */
`;
